<template lang="pug">
.organization-member
  #members-back-nav.d-inline-flex
    a.d-flex.align-items-center.blank-link(
      v-if="currentUserIsManagement"
      :href="membersUrl"
    )
      i.hi.hi-left.hi-16.text-blue.mr-5
      | Members

  .my-10
    MemberSettings(
      ref="MemberSettings"
      :selected-tab="selectedTab"
      :processing-in-background="processingInBackground"
      @tab-selected="forwardTabSelected"
    )
      template(#masthead)
        .d-flex.justify-content-between.align-items-center.max-w-130rem.mb-20.flex-wrap
          h3.mr-15.ellipsis {{ fullName }}
          .d-flex.gap-15.ml-auto.profile-actions(v-if="canEditProfile")
            AppDropdown(
              v-if="canResetPassword || canEditFields"
              label="Actions")
              AppDropdownItem(
                v-if="canResetPassword"
                v-track:click="passwordResetTrack"
                @click="onShowResetPasswordDialog")
                .d-flex.align-items-center.gap-10
                  i.hi.hi-refresh.text-grey
                  | Reset Password...
              li(v-if="canEditFields")
                a(:href="memberProfilesLink")
                  .d-flex.align-items-center.gap-10
                    i.hi.hi-settings.text-grey
                    | Manage profile fields
            button.btn.btn-primary.d-block.sm-w-auto(
              v-if="!isTabReadOnly"
              :disabled="submitting"
              @click.stop.prevent="processMembershipDataUpdate"
            )
              | {{ !submitting ? 'Save Changes' : 'Saving...' }}
      template(#actions)
        .keep

  MemberOccupationChangeDialog(
    v-if="showMemberOccupationChangeDialog"
    @close="closeMemberOccupationChangeDialog"
    @confirm="onConfirmMemberOccupationChange"
  )

  MemberResetPasswordDialog(
    v-if="showResetPasswordDialog"
    @close="closeResetPasswordDialog"
  )

  MemberSendResetPasswordDialog(
    v-if="showSendResetPasswordDialog"
    @close="closeSendResetPasswordDialog"
  )

  ConfirmSeatsIncreaseDialog(
    v-if="showIncreasedSeatDialog"
    @confirm="confirmUpdateMembership"
    @close="cancelUpdateMembership"
  )

  MemberRatesReviewDialog(
    v-if="showRateReviewDialog"
    :sections="rateReviewSections"
    :member-name="membership.user.name"
    :currency="organization.currency"
    @confirm="onRateReviewConfirm"
    @cancel="onRateReviewCancel"
  )
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { extend } from '~/utils/vee_validate_compat';

import { handleFormErrors } from '~/utils/form_utils';
import MemberSettings from './MemberSettings.vue';
import MemberOccupationChangeDialog from './MemberOccupationChangeDialog.vue';
import MemberResetPasswordDialog from './MemberResetPasswordDialog.vue';
import MemberSendResetPasswordDialog from './MemberSendResetPasswordDialog.vue';
import ConfirmSeatsIncreaseDialog from './ConfirmSeatsIncreaseDialog.vue';
import MemberRatesReviewDialog from './MemberRatesReviewDialog.vue';
import { rateReviewSections as buildRateReviewSections } from './memberRateReview';
import validations from '~/vee-validations';

import dayjs from 'dayjs';
import dayjsRelativeTime from 'dayjs/plugin/relativeTime';
import { OrganizationsResource } from '~/resources';
import { initChannelConnection } from '~/channels/init_channel_connection';
import { isEmpty } from 'lodash-es';

dayjs.extend(dayjsRelativeTime);

extend('greaterThanZero', validations.greaterThanZero);

export default {
  name: 'TheOrganizationMemberDialog',
  components: {
    MemberSettings,
    MemberResetPasswordDialog,
    MemberSendResetPasswordDialog,
    MemberOccupationChangeDialog,
    ConfirmSeatsIncreaseDialog,
    MemberRatesReviewDialog,
  },
  props: {
    selectedTab: {
      type: String,
    },
    selectedSection: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showResetPasswordDialog: false,
      showSendResetPasswordDialog: false,
      showMemberOccupationChangeDialog: false,
      showIncreasedSeatDialog: false,
      updateActivityHistoricalData: false,
      initialOccupationId: null,
      passwordResetTrack: Object.freeze({ label: 'Member password reset', action: 'password_reset' }),
      fullName: null,
      initialFirstName: null,
      initialLastName: null,
      showRateReviewDialog: false,
      rateChangesReviewed: false,
      processingInBackground: this.$store.state.membership.processing_in_background,
      submittedUpdate: false,
    };
  },
  computed: {
    ...mapState(['errors', 'membership', 'submitting', 'changes', 'organization']),
    ...mapState({
      currentUser: 'current_user',
      editUserUrl: 'edit_user_url',
      membersUrl: 'members_url',
      user: (state) => state.membership.user,
    }),
    ...mapGetters([
      'membershipData',
      'paymentsData',
      'currentUserIsManagement',
      'organizationId',
      'canEditProfile',
      'canResetPassword',
      'canEditFields',
    ]),
    // CURRENT -> UPDATED comparison for the "Review your changes" dialog.
    rateReviewSections() {
      return buildRateReviewSections(this.membership.payments);
    },
    isTabReadOnly() {
      return this.selectedTab === 'projectsRoles' && !this.currentUserIsManagement;
    },
    formattedTimeZone() {
      return this.user.time_zone.formatted_timezone;
    },
    lastTrackedTimeText() {
      const lastActivity = dayjs(this.membership.last_activity_at).fromNow(true);

      return `${lastActivity} ago`;
    },
    lastTrackedTimeTooltip() {
      let tooltip = dayjs(this.membership.last_activity_at).format('ddd, MMM D, YYYY h:mm a');

      if (this.membership.last_activity_project) {
        tooltip += `<br /> ${HubstaffUtils.escapeHTML(this.membership.last_activity_project)}`;
        if (this.membership.last_activity_task) {
          tooltip += ` / ${HubstaffUtils.escapeHTML(this.membership.last_activity_task)}`;
        }
      }

      return tooltip;
    },
    editUserHref() {
      return this.editUserUrl.replace('__USER_ID__', this.user.id);
    },
    isCurrentUser() {
      return this.currentUser.id === this.user.id;
    },
    editLinkComponent() {
      return this.isCurrentUser
        ? `<a class="d-flex text-lighter ml-10 a-tooltip" href="${this.editUserHref}" data-placement="right" title="Open Hubstaff Account"><i class="hi hi-12 hi-external"></i></a>`
        : `<i class="hi hi-12 hi-info-filled a-tooltip text-lighter ml-10" v-else="v-else" data-placement="right" title="This Hubstaff account data can only be changed by the member"></i>`;
    },
    memberProfilesLink() {
      return OrganizationsResource.profilesSettingsLink({ id: this.organizationId });
    },
  },
  watch: {
    selectedSection(newVal) {
      if (newVal) {
        const element = document.getElementById(newVal);

        this.$nextTick(() => {
          if (element) {
            const navHeight = document.getElementById('tabs-container').offsetHeight + 50;
            const topPos = element.getBoundingClientRect().top + window.pageYOffset - navHeight;

            window.scrollTo({ top: topPos, behavior: 'smooth' });
          }
        });
      }
    },
    '$store.state.membership.processing_in_background'(newVal) {
      this.processingInBackground = newVal;
    },
  },
  beforeMount() {
    this.setMemberRates(this.membership.id);
    this.initialOccupationId = this.membership.occupation_id;
    this.fullName = this.membership.user.name;
    this.initialFirstName = this.membership.user.first_name;
    this.initialLastName = this.membership.user.first_name;
  },
  mounted() {
    this.connection = initChannelConnection({
      channel: 'UserOrganizations::MembershipsLockChannel',
      organizationId: this.membership.organization_id,
      userId: this.$store.state.current_user.id,
      recordId: this.membership.id,
      onConnected: this.loadMembershipsLock,
      onReceived: this.onMessageReceived,
    });
  },
  beforeUnmount() {
    this.connection?.unsubscribe();
  },
  methods: {
    ...mapActions(['updateMembership', 'setSubmitting', 'setMemberRates', 'resetFields', 'loadDialogState']),
    forwardTabSelected(...args) {
      this.$emit('tab-selected', ...args);
    },
    submitUpdateMembership({ confirmedIncreasedSeat = false } = {}) {
      const payload = {
        ...this.membershipData,
        update_activity_historical_data: this.updateActivityHistoricalData,
        confirmed_increased_seat: confirmedIncreasedSeat,
      };

      return this.updateMembership({ data: payload });
    },

    confirmUpdateMembership() {
      this.showIncreasedSeatDialog = false;
      this.submitUpdateMembership({ confirmedIncreasedSeat: true }).then(this.handleUpdateMembershipResponse, this.handleErrors);
    },

    cancelUpdateMembership() {
      this.showIncreasedSeatDialog = false;
      this.setSubmitting(false);
      this.$appMessage('Reverted changes');
      this.loadDialogState(this.membership.id);
    },

    async handleSubmit() {
      this.$refs.MemberSettings.clearBackendErrors();
      const validated = await this.$refs.MemberSettings.$refs.dialogObserver.validate();

      if (validated) {
        if (!this.$refs.MemberSettings.validateCustomPayPeriodSelection()) {
          return;
        }

        if (this.rateReviewSections.length && !this.rateChangesReviewed) {
          this.showRateReviewDialog = true;

          return;
        }

        this.rateChangesReviewed = false;
        this.setSubmitting(true);
        this.submittedUpdate = true

        try {
          const response = await this.submitUpdateMembership();

          this.handleUpdateMembershipResponse(response);
        } catch {
          this.handleErrors();

          // Wait for re-render to focus on the failed tab
          setTimeout(() => this.$refs.MemberSettings.focusFailedTab(), 50);
        }
      }
    },
    async handleTabSubmit() {
      this.$refs.MemberSettings.clearBackendErrors();
      const validated = await this.$refs.MemberSettings.$refs[this.selectedTab].validate();
      const tabData = `${this.selectedTab}Data`;

      if (validated) {
        if (!this.$refs.MemberSettings.validateCustomPayPeriodSelection()) {
          return;
        }

        this.setSubmitting(true);
        this.updateMembership({ data: { [this.selectedTab]: this[tabData] }, tab: this.selectedTab }).then(
          this.completeRequest,
          this.handleErrors,
        );
      }
    },
    handleUpdateMembershipResponse(response) {
      if (response?.confirm_increased_seat === true) {
        this.showIncreasedSeatDialog = true;

        return;
      }

      this.completeRequest(response);
    },
    completeRequest(response) {
      this.setSubmitting(false);

      if (response && response.success) {
        this.handleSuccess(response);
      } else {
        this.handleErrors();
      }
    },
    handleSuccess(response) {
      this.trackUpdateChanges();
      this.$appMessage(response.message || 'Information updated');
      this.reflectNameChange();
      this.loadDialogState(this.membership.id);
      window.dispatchEvent(new CustomEvent('pay-period:saved'));

      // Show payroll advertisement modal if triggered
      if (response.payroll_advertisement_modal_url) {
        const modalFrame = document.querySelector('turbo-frame#modal');

        if (modalFrame) {
          modalFrame.src = response.payroll_advertisement_modal_url;
        }
      }
    },
    loadMembershipsLock() {
      this.connection.subscription.send({ load: true })
    },
    onMessageReceived(data = {}) {
      // Lock the form fields if user has submitted update
      if (data.locked && this.submittedUpdate) {
        this.processingInBackground = true

        return
      }

      if (data.locked === false) {
        // Cleanup submittedUpdate flag
        this.submittedUpdate = false

        // Unlock the form fields
        if (this.processingInBackground) {
          this.loadDialogState(this.membership.id);
          this.$appMessage('Information updated');
          this.processingInBackground = false
        }
      }
    },
    reflectNameChange() {
      const user = this.membership.user;

      if (user.first_name !== this.initialFirstName || user.last_name !== this.initialLastName) {
        this.fullName = `${user.first_name} ${user.last_name}`;
      }
    },
    handleErrors() {
      const form = this.$refs.MemberSettings.$refs.dialogObserver;

      // Tab field errors only reach their fields via the per-tab observers (vee-validate v4);
      // MemberSettings filters and applies them there and marks the tab red.
      this.$refs.MemberSettings.applyBackendErrors(this.errors);

      this.handlePayRateErrors(form);

      // Filter out note errors before passing to handleFormErrors
      const filteredErrors = Object.fromEntries(
        Object.entries(this.errors).filter(
          ([key]) => key !== 'pay_rate_note' && key !== 'bill_rate_note'
        )
      );

      if (!isEmpty(filteredErrors)) {
        handleFormErrors(form, filteredErrors, this.$appMessage);
      }

      this.handleAddressErrors(form);
    },
    handlePayRateErrors(form) {
      if (this.errors.pay_rate_note) {
        this.$appMessage(this.errors.pay_rate_note);
      }

      if (this.errors.bill_rate_note) {
        this.$appMessage(this.errors.bill_rate_note);
      }
    },
    handleAddressErrors(form) {
      if (this.errors.home_address) {
        handleFormErrors(form, { homeAddress: this.errors.home_address }, this.$appMessage);
      }

      if (this.errors.work_address) {
        handleFormErrors(form, { workAddress: this.errors.work_address }, this.$appMessage);
      }
    },
    onShowResetPasswordDialog() {
      if (this.membership.from_account_provisioning && !this.user.allow_receiving_emails) {
        this.showResetPasswordDialog = true;
      } else {
        this.showSendResetPasswordDialog = true;
      }
    },
    closeResetPasswordDialog() {
      this.showResetPasswordDialog = false;
    },
    closeSendResetPasswordDialog() {
      this.showSendResetPasswordDialog = false;
    },
    closeMemberOccupationChangeDialog() {
      this.showMemberOccupationChangeDialog = false;
    },
    onConfirmMemberOccupationChange(updateHistoricalData) {
      this.updateActivityHistoricalData = updateHistoricalData;
      this.showMemberOccupationChangeDialog = false;

      this.handleSubmit();
    },
    onRateReviewConfirm() {
      this.showRateReviewDialog = false;
      this.rateChangesReviewed = true;
      this.handleSubmit();
    },
    onRateReviewCancel() {
      this.showRateReviewDialog = false;
      this.rateChangesReviewed = false;
    },
    processMembershipDataUpdate() {
      const occupationChanged = this.membership.occupation_id !== this.initialOccupationId;

      if (occupationChanged) {
        this.showMemberOccupationChangeDialog = true;
        this.initialOccupationId = this.membership.occupation_id;
      } else {
        this.handleSubmit();
      }
    },
    trackUpdateChanges() {
      const changedToTrack = [
        'employment_type',
        'in_office_percent',
        'remote_percent',
        'workplace_model',
        'payments',
        'department',
        'employment_comment',
        'started_on',
        'stopped_on',
        'limits',
        'custom_fields',
      ].reduce((acc, key) => {
        if (key in this.changes) {
          if (key === 'payments' || key === 'limits') {
            for (const [innerKey, value] of Object.entries(this.changes[key])) {
              acc[innerKey] = value;
            }
          } else if (key === 'custom_fields') {
            acc.custom_fields = Object.entries(this.changes[key])
              .map(([fieldKey, value]) => `${fieldKey}:${value.replace(/\|/g, '\\|')}`)
              .join(' | ');
          } else {
            acc[key] = this.changes[key];
          }
        }

        return acc;
      }, {});

      // We are still tracking that the member is updated even if they didn't update any of the above fields
      window.analytics?.track('Member updated', changedToTrack);
    },
  },
};
</script>
